import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import { OrderData, ListingTableColumn, FilterData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Loading from 'common/services/Loading';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaEdit, FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import Logger from 'common/services/Logger';
import { Reducers } from 'store/types';
import Toast from 'common/services/Toast';
import Button from 'common/components/button/Button';
import { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';
import { CatalogParameterSearchCriteria } from 'api/catalogParameters/models/CatalogParameterSearchCriteria';
import CatalogParametersService from 'api/catalogParameters/CatalogParametersService';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { ParameterSectionParameterType } from 'common/components/parametersSections/models';

const PAGE_ID = 'LIBRARY_CATALOG_PARAMETERS_LIST';
const baseUrl = '/library/catalog-parameters';

function CatalogParametersListScreen (): JSX.Element | null {
    const { t } = useTranslation();
    const [tableData, setTableData] = useState<ListingTableData<CatalogParameterDto, Partial<CatalogParameterSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'name', orderColumn: 'cp.name', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const [items, setItems] = useState<CatalogParameterDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [itemToDelete, setItemToDelete] = useState<CatalogParameterDto | null>(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['CATALOG_PARAMETERS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['CATALOG_PARAMETERS_READ']);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const columns: ListingTableColumn<CatalogParameterDto>[] = orderColumns([
        {
            name: t('catalog_parameters.list.name'),
            field: 'name',
            filter: { type: 'text', value: tableData.filters.name ?? '', placeholder: t('catalog_parameters.list.name') },
            minWidth: '18rem',
        },
        {
            name: t('catalog_parameters.list.parameter_type'),
            field: 'parameterType',
            filter: {
                type: 'dropdown',
                options: Object.keys(ParameterSectionParameterType)
                    .filter(key => key !== ParameterSectionParameterType.NUMBER_MIN_MAX)
                    .map(key => ({
                        label: t('common.parameters_sections.parameters_types.' + key),
                        value: key,
                    })).sort((a, b) => a.label.localeCompare(b.label)),
                value: tableData.filters.parameterType ?? null,
            },
            minWidth: '14rem',
            orderColumn: 'cp.parameter_type',
            renderCell: (row) => {
                return row.parameterType ? t('common.parameters_sections.parameters_types.' + row.parameterType) : '';
            }
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDate?.start ? new Date(tableData.filters.updatedDate.start) : null,
                    end: tableData.filters.updatedDate?.end ? new Date(tableData.filters.updatedDate.end) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '21rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const renderAction = (row: CatalogParameterDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, href: `${baseUrl}/edit/${row.id}`, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaCopy /> {t('common.duplicate')}</span>, href: `${baseUrl}/duplicate/${row.id}`, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => onOpenModalToDelete(row) },
            ]}
        />
    );

    const onFilter = (col: ListingTableColumn<CatalogParameterDto>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({ ...d, page: 1 });
            if (col.field === 'name') {
                data.filters = { ...data.filters, name: filterData.value as string };
            }
            if (col.field === 'parameterType') {
                data.filters = { ...data.filters, parameterType: filterData.value as string };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDate: filterData.value,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date
                };
            }
            if (col.field === 'userUpdatedName') {
                data.filters = { ...data.filters, userUpdatedName: filterData.value as string };
            }
            return data;
        });
        setRefreshKey(k => k + 1);
    };

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    };

    const onOrder = (orderData: OrderData<CatalogParameterDto>) => {
        setRefreshKey(k => k + 1);
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
    };

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    const getData = async () => {
        try {
            if (isLoading) return;

            Loading.show();
            setIsLoading(true);

            const data = await CatalogParametersService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc'
            });

            const newListElements = data.currentPage === 1 ? data.items : [...items, ...data.items];
            setHasMore(data.totalItems > newListElements.length);
            setItems(newListElements);
            setTotalItems(data.totalItems);

            if (firstLoad.current) {
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get catalog parameters list', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setIsLoading(false);
        }
    };

    const onRowClick = (row: CatalogParameterDto) => {
        ListingTableCache.saveLastRowId(PAGE_ID, row.id);
    };

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    };

    const onOpenModalToDelete = (row: CatalogParameterDto) => {
        setItemToDelete(row);
        setShowConfirmDeleteModal(true);
    };

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false);
            return;
        }
        try {
            await CatalogParametersService.remove(itemToDelete as CatalogParameterDto);
            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);
            Loading.hide();
            Toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete catalog parameter', error);
            Toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false);
    };

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('catalog_parameters.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('catalog_parameters.title'), url: '/library/catalog-parameters' },
                ]}
            />
            <PageHeader title={t('catalog_parameters.title')} subTitle={`${totalItems} ${t('common.total_results')}`} addSidebarSpacing informationText={t('common.list')}>
                {canWrite && <Button href={`${baseUrl}/create`}>{t('common.new')}</Button>}
            </PageHeader>
            <PageContainer>
                <ListingTable<CatalogParameterDto>
                    columns={columns}
                    rows={items}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    onRowClick={onRowClick}
                    onHref={(row) => `${baseUrl}/details/${row.id}`}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore,
                    }}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
            <ConfirmDeleteModal
                itemName={itemToDelete?.name}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default CatalogParametersListScreen;
